import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PortalAdminLayout from '../admin/PortalAdminLayout';
import './PortalAdminStyles.css';

const BusinessTypesManagement = () => {
  const navigate = useNavigate();
  const [businessTypes, setBusinessTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    value: '',
    label: '',
    emoji: '',
    description: '',
    active: true
  });

  useEffect(() => {
    fetchBusinessTypes();
  }, []);

  const fetchBusinessTypes = async () => {
    try {
      const token = localStorage.getItem('ogestyle_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/business-categories/all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBusinessTypes(response.data.types || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingType(null);
    setFormData({
      value: '',
      label: '',
      emoji: '',
      description: '',
      active: true
    });
    setShowAddModal(true);
  };

  const handleEdit = (type) => {
    setEditingType(type.value);
    setFormData({
      value: type.value,
      label: type.label,
      emoji: type.emoji,
      description: type.description || '',
      active: type.active
    });
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('ogestyle_token');
      const url = editingType
        ? `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/business-categories/${editingType}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/business-categories`;
      
      const method = editingType ? 'put' : 'post';
      
      await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowAddModal(false);
      fetchBusinessTypes();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to save business category');
    }
  };

  const handleDelete = async (value) => {
    if (!window.confirm('Are you sure you want to delete this business category? This will affect businesses using this category.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('ogestyle_token');
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/business-categories/${value}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchBusinessTypes();
    } catch (error) {
      alert('Failed to delete business type');
    }
  };

  const toggleActive = async (type) => {
    try {
      const token = localStorage.getItem('ogestyle_token');
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/business-categories/${type.value}`,
        { ...type, active: !type.active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchBusinessTypes();
    } catch (error) {
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('ogestyle_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/business-categories/export`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `business-categories-export-${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Failed to export business categories');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      if (!data.businessCategories || !Array.isArray(data.businessCategories)) {
        alert('Invalid import file format');
        return;
      }

      const mode = window.confirm(
        `Import ${data.businessCategories.length} categories.\n\n` +
        `Click OK to MERGE with existing (recommended)\n` +
        `Click Cancel to REPLACE all existing categories`
      ) ? 'merge' : 'replace';

      const token = localStorage.getItem('ogestyle_token');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/business-categories/import`,
        { businessCategories: data.businessCategories, mode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`Successfully imported ${data.businessCategories.length} categories (${mode} mode)`);
      fetchBusinessTypes();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to import business categories');
    }
    
    // Reset file input
    e.target.value = '';
  };

  if (loading) {
    return (
      <PortalAdminLayout>
        <div className="portal-admin-loading">Loading...</div>
      </PortalAdminLayout>
    );
  }

  return (
    <PortalAdminLayout>
      <div className="portal-admin-container">
      <div className="portal-admin-header">
        <div>
          <h1 className="portal-admin-title">Business Categories</h1>
          <p className="portal-admin-subtitle">
            Manage business categories available across the platform ({businessTypes.length} total)
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <label className="btn-secondary" style={{ cursor: 'pointer' }}>
            📥 Import
            <input 
              type="file" 
              accept=".json" 
              onChange={handleImport} 
              style={{ display: 'none' }}
            />
          </label>
          <button className="btn-secondary" onClick={handleExport}>
            📤 Export
          </button>
          <button className="btn-primary" onClick={openAddModal}>
            + Add Business Category
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {businessTypes.map(type => (
          <div key={type.value} style={{
            display: 'flex',
            alignItems: 'center',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '16px 20px',
            gap: '16px',
            transition: 'box-shadow 0.2s',
            ':hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }
          }}>
            <div style={{ fontSize: '2rem' }}>{type.emoji}</div>
            
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1a1a1a' }}>
                  {type.label}
                </h3>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '600',
                  background: type.active ? '#10b981' : '#ef4444',
                  color: 'white'
                }}>
                  {type.active ? 'ACTIVE' : 'INACTIVE'}
                </span>
              </div>
              
              {type.description && (
                <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>{type.description}</p>
              )}
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '8px' }}>
                <span style={{ fontSize: '11px', color: '#999', fontFamily: 'monospace' }}>
                  ID: {type.value}
                </span>
                
                {type.roles && type.roles.length > 0 && (
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {type.roles.slice(0, 5).map((role, idx) => (
                      <span key={idx} style={{
                        padding: '2px 6px',
                        background: '#f3f4f6',
                        borderRadius: '3px',
                        fontSize: '11px',
                        color: '#4b5563'
                      }}>{role.label}</span>
                    ))}
                    {type.roles.length > 5 && (
                      <span style={{
                        padding: '2px 6px',
                        background: '#f3f4f6',
                        borderRadius: '3px',
                        fontSize: '11px',
                        color: '#4b5563'
                      }}>+{type.roles.length - 5} more</span>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
              <button 
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  background: type.active ? '#fef3c7' : '#d1fae5',
                  color: type.active ? '#92400e' : '#065f46'
                }}
                onClick={() => toggleActive(type)}
              >
                {type.active ? 'Deactivate' : 'Activate'}
              </button>
              <button 
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  background: '#dbeafe',
                  color: '#1e40af'
                }}
                onClick={() => handleEdit(type)}
              >
                Edit
              </button>
              <button 
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  background: '#fee2e2',
                  color: '#991b1b'
                }}
                onClick={() => handleDelete(type.value)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">
              {editingType ? 'Edit Business Category' : 'Add Business Category'}
            </h2>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label>Value (ID)</label>
                <input
                  type="text"
                  value={formData.value}
                  onChange={(e) => setFormData({...formData, value: e.target.value})}
                  placeholder="e.g., beauty_salon"
                  disabled={editingType}
                  required
                />
                <small>Lowercase, underscores only. Cannot be changed after creation.</small>
              </div>

              <div className="form-group">
                <label>Display Label</label>
                <input
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData({...formData, label: e.target.value})}
                  placeholder="e.g., Beauty Salon"
                  required
                />
              </div>

              <div className="form-group">
                <label>Emoji Icon</label>
                <input
                  type="text"
                  value={formData.emoji}
                  onChange={(e) => setFormData({...formData, emoji: e.target.value})}
                  placeholder="e.g., 💄"
                  maxLength="2"
                />
                <small>Single emoji character</small>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description of this business type"
                  rows="3"
                />
              </div>

              <div className="form-group-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({...formData, active: e.target.checked})}
                  />
                  <span>Active (visible to users)</span>
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingType ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </PortalAdminLayout>
  );
};

export default BusinessTypesManagement;
